import type { Expression } from './expression';
import type { Face } from './faces';
import { blinkAt, Eye } from './HumanFace';

type UnicornPose = {
  /** Ear splay from vertical, degrees. */
  earAngle: number;
  /** Whole-head tilt, degrees. Negative leans in. */
  headTilt: number;
  /** How far the mane trails the head, 0..1. */
  maneSway: number;
  look: [number, number];
  mouth: number;
};

/**
 * The five poses.
 *
 * Deliberately not the capybara's numbers. A capybara's ears sit flat on a wide
 * skull and carry nearly its whole expression; a horse's stand upright on a
 * narrow one and swing through a much larger arc, and it has a neck to move as
 * well. Copying `CreatureFace`'s values across would give a capybara in a
 * costume — which is why this is a second rig and not a flag on that one.
 */
const POSES: Record<Expression, UnicornPose> = {
  neutral: { earAngle: 6, headTilt: 0, maneSway: 0, look: [0, 0], mouth: 0.12 },
  // Ears swivelled hard forward, head dropped toward the learner. On a horse
  // this is the unmistakable one — ears forward is attention, and it survives
  // at 38px where an eye movement does not.
  listening: {
    earAngle: 30,
    headTilt: -11,
    maneSway: 0.3,
    look: [0, 0.6],
    mouth: 0.05,
  },
  // §8.1 asks for stillness. Ears part, gaze up and away, mane settled —
  // absence of movement is the signal, so nothing here sits at its extreme.
  thinking: {
    earAngle: 14,
    headTilt: 2,
    maneSway: 0,
    look: [0.8, -0.9],
    mouth: 0.08,
  },
  speaking: {
    earAngle: 8,
    headTilt: 3,
    maneSway: 0.5,
    look: [0, 0.2],
    mouth: 0.9,
  },
  // Ears out sideways, not pinned back. Flat-back is a threat display on a
  // horse; this character must never look angry at a six-year-old who was
  // simply not understood.
  concern: {
    earAngle: -20,
    headTilt: 12,
    maneSway: 0.2,
    look: [0, 0.45],
    mouth: 0.22,
  },
  // A head toss: ears up, chin lifted, mane thrown. The only pose that uses
  // the neck, because the ears alone have already been spent on `listening`.
  celebrate: {
    earAngle: 24,
    headTilt: -6,
    maneSway: 1,
    look: [0, 0.1],
    mouth: 0.6,
  },
};

/**
 * The unicorn rig. Same props and the same five expressions as the other two,
 * so `TutorAvatar` picks between three renderers without any of them leaking
 * through the interface — the promise Q1 makes.
 *
 * What reads as a unicorn, in the order a viewer resolves it: the horn, a head
 * clearly taller than wide that tapers to a muzzle, upright ears, and a mane.
 * Only the horn is unique to the animal, so it is drawn tallest, dead centre,
 * and last — §8.3's claim that identity survives at 38px in the silhouette and
 * nowhere else.
 *
 * The first version of this file failed exactly that test: the mane was two
 * symmetrical lobes flanking the head, which at any size read as floppy ears
 * and buried both the real ears and the horn. Rendered side by side with Cappy
 * it was a pink pig. Hence a mane that is two narrow strands *behind* the head
 * plus a forelock, and a horn nearly a third of the viewBox tall.
 */
export function UnicornFace({ face, expression, phase }: { face: Face; expression: Expression; phase: number }) {
  const breathe = Math.sin(phase / 1500);
  const blink = blinkAt(phase);
  const pose = POSES[expression];
  const w = face.headWidth;
  const half = w / 2;

  return (
    <svg
      viewBox="0 0 100 100"
      className="h-full w-full"
      role="presentation"
      style={{
        transform:
          `translateY(calc(${breathe.toFixed(3)} * var(--tutor-intensity) * 1.3px)) ` +
          `rotate(calc(${pose.headTilt}deg * var(--tutor-intensity)))`,
        // Low, where a neck would be: the head swings from the shoulder rather
        // than spinning about its own middle, which is what the capybara does
        // and what looks wrong on anything with a long face.
        transformOrigin: '50px 92px',
      }}
    >
      <Mane color={face.hairColor} sway={pose.maneSway} phase={phase} />
      {/* Crown, then a taper to a rounded chin: 44 wide and 66 tall. The head
          and muzzle are one shape here, unlike the capybara's, where the
          muzzle is a separate block sitting proud of a flat-topped skull. */}
      <path
        d={`M${50 - half} 46 a${half} ${half} 0 0 1 ${w} 0 ` + `L66 74 Q66 90 50 90 Q34 90 34 74 Z`}
        fill={face.skin}
      />
      {/* A shade lighter, and the reason the mouth has something to sit on. */}
      <ellipse cx="50" cy="79" rx="14" ry="10" fill="#FDE9F2" />
      {/* After the head, not before it. Drawn behind, the crown swallowed all
          but the tip and `listening` — which is carried almost entirely by
          ears-forward — became indistinguishable from `neutral`. */}
      <Ear cx={50 - half + 6} angle={pose.earAngle} coat={face.skin} inner={face.hairColor} />
      <Ear cx={50 + half - 6} angle={-pose.earAngle} coat={face.skin} inner={face.hairColor} />
      <Forelock color={face.hairColor} />
      <Horn />
      <Eye cx={50 - half + 9} cy={54} aspect={face.eye} open={blink} look={pose.look} />
      <Eye cx={50 + half - 9} cy={54} aspect={face.eye} open={blink} look={pose.look} />
      <Muzzle mouth={pose.mouth} phase={phase} speaking={expression === 'speaking'} />
    </svg>
  );
}

/**
 * The horn. Fixed gold, not `Face`-driven.
 *
 * A per-character horn colour would be a knob no character uses: there is one
 * unicorn, and a second would want its own outline and pose set rather than
 * this one in another colour.
 *
 * Drawn last of the head parts so it sits over the forelock, which is how it
 * grows — and tall enough (a third of the viewBox) that it, not the colour, is
 * what identifies the character in a row of thumbnails.
 */
function Horn() {
  return (
    <g>
      <path d="M50 3 l6 30 h-12 z" fill="#FDE68A" />
      {/* Two stripes, not a spiral. A spiral costs a dozen path commands and
          dissolves below about 60px; two read at every size this is drawn at. */}
      <path d="M45.6 25 h8.8 M47 16 h6" stroke="#D9A441" strokeWidth="1.2" strokeLinecap="round" fill="none" />
    </g>
  );
}

function Ear({ cx, angle, coat, inner }: { cx: number; angle: number; coat: string; inner: string }) {
  return (
    <g
      style={{
        transform: `rotate(calc(${angle}deg * var(--tutor-intensity)))`,
        transformOrigin: `${cx}px 42px`,
      }}
    >
      <path d={`M${cx} 12 l8 30 h-16 z`} fill={coat} />
      <path d={`M${cx} 21 l3.5 17 h-7 z`} fill={inner} />
    </g>
  );
}

/**
 * Two strands down the sides, behind the head.
 *
 * Behind, and narrow, for the reason in the component doc: anything wide and
 * symmetrical beside a head reads as ears, and this rig already has ears that
 * need to be seen. They are about seven units across at the widest and stop
 * above the chin — the first pass ran to the full height of the face and read
 * as pigtails, which is the same mistake in a quieter form.
 *
 * `sway` is a pose value rather than a function of the head tilt — the mane
 * lags the head, so deriving it from the tilt would have the two arrive
 * together and read as one rigid piece. A slow idle drift on top keeps it
 * alive in `neutral`, where everything else is still.
 */
function Mane({ color, sway, phase }: { color: string; sway: number; phase: number }) {
  const drift = Math.sin(phase / 2200) * 2;
  return (
    <g
      style={{
        transform: `rotate(calc((${(sway * 7).toFixed(2)} + ${drift.toFixed(2)})deg * var(--tutor-intensity)))`,
        transformOrigin: '50px 80px',
      }}
    >
      <path d="M33 36 C25 46 23 62 27 78 L34 73 C31 59 32 47 38 39 Z" fill={color} />
      <path d="M67 36 C75 46 77 62 73 78 L66 73 C69 59 68 47 62 39 Z" fill={color} />
    </g>
  );
}

/**
 * The forelock: two locks parting around the horn.
 *
 * Was a zigzag fringe, which at 300px read as a row of teeth and at 38px as
 * noise around the horn — the one feature that must stay legible. Two soft
 * crescents sweeping outward do the same job and leave the horn alone.
 *
 * Stops at y=48 because the eyes are at 54. The human rig's `Hair` carries the
 * same note for the same reason: hair that reaches the eyes hides them, and it
 * takes rendering one to see it.
 */
function Forelock({ color }: { color: string }) {
  return (
    <g fill={color}>
      <path d="M48 28 C40 30 34 38 34 48 C36 40 41 34 48 33 Z" />
      <path d="M52 28 C60 30 66 38 66 48 C64 40 59 34 52 33 Z" />
    </g>
  );
}

function Muzzle({ mouth, phase, speaking }: { mouth: number; phase: number; speaking: boolean }) {
  // Speech is the clock, not the audio envelope — the same claim the other two
  // rigs make. It reads as talking without pretending to be lipsync.
  const talk = speaking ? 0.5 + 0.5 * Math.abs(Math.sin(phase / 90)) : 1;
  const h = mouth * talk * 8;
  return (
    <g>
      <ellipse cx="45" cy="75" rx="2" ry="2.6" fill="#D98BAE" />
      <ellipse cx="55" cy="75" rx="2" ry="2.6" fill="#D98BAE" />
      <path d={`M45 83 q5 ${h + 2} 10 0`} fill="none" stroke="#C2708F" strokeWidth="2" strokeLinecap="round" />
    </g>
  );
}
